import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import {DateTime} from 'luxon'
import RadioGroup, {RadioGroupProps} from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import styled from 'styled-components'
import ButtomAtom from '../button/button.atom'
import button_timeslotStyles from './button_timeslot.styles'
import TypographyAtom from '../../to_content/typography/typography.atom'
import StackAtom from '../../to_layout/stack/stack.atom'
import FieldsetAtom from '../fieldset/fieldset.atom'
import SvgIconAtom from '../../to_content/svg_icon/svg_icon.atom'
import {
  BookedSlotDetails,
  DateTimeSlotProps,
} from '../../../props_reusable/visit'

interface ApRadioGroupProps extends RadioGroupProps {
  ap_data_arr: DateTimeSlotProps[]
  // eslint-disable-next-line
  setSelectedSlot?: (arg: DateTimeSlotProps) => void
}

interface ButtonTimeSlotHandle {
  getSlot: () => DateTimeSlotProps | null
  removeSelected: () => void
  setDefaultValue: (arg: BookedSlotDetails) => void
  selectByIndex: (arg: number) => void
}

const apAtomName = {ap_atom_name: 'buttonTimeslotAtom'}

interface TimeSlotProps {
  time: string
  echo: boolean
  selected: boolean
}

const TimeSlotLabel = styled((props: TimeSlotProps): JSX.Element => {
  return (
    <StackAtom ap_type="row">
      <div>
        <TypographyAtom
          ahd_type={props.selected ? 'selectedTime' : 'selectableTime'}
        >
          {props.time}
        </TypographyAtom>
      </div>

      {props.echo && (
        <div>
          <SvgIconAtom ap_type="greenLeaf" ap_color="inherit" />
        </div>
      )}
    </StackAtom>
  )
})`
  ${button_timeslotStyles};
`

const ButtonContainer = styled(props => {
  return <ButtomAtom {...props}>{props.children}</ButtomAtom>
})`
  ${button_timeslotStyles};
`

const ButtonTimeslotAtom: ForwardRefRenderFunction<
  ButtonTimeSlotHandle,
  ApRadioGroupProps
> = function ({ap_data_arr, setSelectedSlot}, ref) {
  const selectedDate = ap_data_arr?.[0]?.date || ''
  const [index, setIndex] = useState<number | null>(null)
  const handleSlotChange = (i: number) => {
    setIndex(i)
    if (setSelectedSlot) setSelectedSlot(ap_data_arr[i])
  }

  const setSlotAsSelected = (slotDetails: BookedSlotDetails) => {
    const matchedIndex = ap_data_arr.findIndex(d => {
      return (
        DateTime.fromISO(slotDetails.visitDate).toFormat('LL/dd/yyyy') ===
          DateTime.fromISO(d.date).toFormat('LL/dd/yyyy') &&
        slotDetails.timeSlotId === d.timeSlotId &&
        slotDetails.timeSlotGroupId === d.timeSlotGroupId
      )
    })
    if (matchedIndex >= 0 && !index) {
      setIndex(matchedIndex)
      if (setSelectedSlot) setSelectedSlot(ap_data_arr[matchedIndex])
    }
  }

  const selectByIndex = (p: number) => {
    if (ap_data_arr[p]?.availableToBook) {
      setIndex(p)
      if (setSelectedSlot) setSelectedSlot(ap_data_arr[p])
    }
  }

  useImperativeHandle(ref, () => ({
    getSlot: (): DateTimeSlotProps | null => {
      return index !== null ? ap_data_arr[index] : null
    },
    removeSelected: () => setIndex(null),
    setDefaultValue: setSlotAsSelected,
    selectByIndex,
  }))

  useEffect(() => {
    if (selectedDate) {
      const elem = document.querySelector('#timeslots-fieldset')
      elem?.scrollTo({top: 0, behavior: 'smooth'})
    }
  }, [selectedDate])

  return (
    <FieldsetAtom id="timeslots-fieldset">
      <RadioGroup {...apAtomName} name="radio-buttons-group">
        {ap_data_arr.map((slotData, i) => (
          <ButtonContainer
            disabled={!slotData.availableToBook}
            key={`${slotData.date}-${slotData.timeSlotId}-${slotData.startTime}-${slotData.endTime}`}
            ap_type={i === index ? 'primary' : 'secondary'}
            onClick={() => handleSlotChange(i)}
          >
            <FormControlLabel
              value={`${slotData.startTime} - ${slotData.endTime}`}
              control={
                i === index ? (
                  <div>
                    <SvgIconAtom ap_type="cardCheckSelected" width="18" />
                  </div>
                ) : (
                  <div>
                    <SvgIconAtom ap_type="cardCheckUnselected" width="18" />
                  </div>
                )
              }
              label={
                <TimeSlotLabel
                  time={`${slotData.startTime} - ${slotData.endTime}`}
                  echo={slotData.eco}
                  selected={i === index}
                />
              }
            />
          </ButtonContainer>
        ))}
      </RadioGroup>
    </FieldsetAtom>
  )
}

const RefButtonTimeslotAtom = forwardRef(ButtonTimeslotAtom)

RefButtonTimeslotAtom.defaultProps = {
  setSelectedSlot: (d: DateTimeSlotProps) => {
    // eslint-disable-next-line
    console.log(d)
  },
}

export default RefButtonTimeslotAtom
